'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';

interface NavigatorUAData {
  platform: string;
  brands: Array<{ brand: string; version: string }>;
  mobile: boolean;
  getHighEntropyValues: (hints: string[]) => Promise<{
    platformVersion: string;
  }>;
}

export default function useCheckUnsupported() {
  const { push } = useRouter();

  useEffect(() => {
    const check = async () => {
      const ua = navigator.userAgent;

      const isAndroid = /Android/i.test(ua);
      const isIOS = /iPhone|iPad|iPod/i.test(ua);

      const getAndroidVersion = async (): Promise<number | null> => {
        try {
          const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
      
          if (uaData?.platform === 'Android' && uaData.getHighEntropyValues) {
            const { platformVersion } = await uaData.getHighEntropyValues(['platformVersion']);
            const major = parseInt(platformVersion.split('.')[0]);
            return isNaN(major) ? null : major;
          }
        } catch {
          // ignore
        }
      
        const androidRegex = /Android\s(\d+(?:\.\d+)?)/i;
        const match = androidRegex.exec(navigator.userAgent);
        return match ? parseFloat(match[1]) : null;
      };
      
      const getIOSVersion = (): number | null => {
        const iosRegex = /OS\s(\d+(?:_\d+)?)/i;
        const match = iosRegex.exec(navigator.userAgent);
        return match ? parseFloat(match[1].replace('_', '.')) : null;
      };
      

      const androidVersion = isAndroid ? await getAndroidVersion() : null;
      const iOSVersion = isIOS ? getIOSVersion() : null;

      const isMobile = isAndroid || isIOS;
      const isDesktop = !isMobile;

      const isSpeechRecognitionSupported =
        'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;

      const getBrowserInfo = (): { name: string; version: number } => {
        const chromeMatch = /Chrome\/(\d+)/.exec(ua);
        const edgeMatch = /Edg\/(\d+)/.exec(ua);
        const firefoxMatch = /Firefox\/(\d+)/.exec(ua);
        const safariMatch = /Version\/(\d{1,3})[^\s/]{0,50}Safari\//.exec(ua);

        if (chromeMatch && !edgeMatch) {
          return { name: 'Chrome', version: parseInt(chromeMatch[1]) };
        } else if (edgeMatch) {
          return { name: 'Edge', version: parseInt(edgeMatch[1]) };
        } else if (firefoxMatch) {
          return { name: 'Firefox', version: parseInt(firefoxMatch[1]) };
        } else if (safariMatch && !ua.includes('Chrome')) {
          return { name: 'Safari', version: parseInt(safariMatch[1]) };
        } else {
          return { name: 'Unknown', version: 0 };
        }
      };

      const { name: browserName, version: browserVersion } = getBrowserInfo();

      const minOSVersions: Record<string, number> = {
        Android: 11,
        iOS: 15,
      };

      const minBrowserVersions: Record<string, number> = {
        Chrome: 114,
        Edge: 114,
        Firefox: 113,
        Safari: 16,
      };

      const isOldMobileOS =
        (isAndroid && androidVersion !== null && androidVersion < minOSVersions.Android) ||
        (isIOS && iOSVersion !== null && iOSVersion < minOSVersions.iOS);

      const isOldBrowser =
        isDesktop &&
        browserName in minBrowserVersions &&
        browserVersion < minBrowserVersions[browserName];

      const isUnsupported =
        !isSpeechRecognitionSupported || isOldMobileOS || isOldBrowser;

      if (isUnsupported) {
        push('/unsupported');
      }
    };

    check();
  }, [push]);
}
